/**
 * Строка набора тем (#845): название, что в неё входит, статус и — для тех,
 * кто правит набор, — «Изменить», «В архив» / «Вернуть». Тема `reserved`
 * (её код занят принятием предложения) не правится: её ведёт принятие.
 * Архивная — только «Вернуть»: её название и определение заморожены, пока
 * она в архиве (сервер ответит 409 и на правку).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgTheme } from '@aica/api-types/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { VoiceThemeForm, type VoiceThemeFormValues } from './VoiceThemeForm';

interface VoiceThemeRowProps {
  theme: OrgTheme;
  canEdit: boolean;
  /** Archiving is always possible; restoring is not when the set is full. */
  canRestore: boolean;
  onEdit: (theme: OrgTheme, values: VoiceThemeFormValues) => Promise<void>;
  onSetArchived: (theme: OrgTheme, archived: boolean) => Promise<void>;
}

export function VoiceThemeRow({
  theme,
  canEdit,
  canRestore,
  onEdit,
  onSetArchived,
}: VoiceThemeRowProps) {
  const { t } = useTranslation('dashboard');
  /**
   * The theme as it was when the edit form OPENED — not as the list shows it
   * now. A refetch while the form is open (another admin's rename) must not
   * move the form's baseline: the changed fields and `expectedUpdatedAt` are
   * taken from this snapshot, so a stale form gets 409 `theme_changed`
   * instead of silently reverting the other edit.
   */
  const [editing, setEditing] = useState<OrgTheme | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleArchived = async (archived: boolean) => {
    setPending(true);
    setError(null);
    try {
      await onSetArchived(theme, archived);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setPending(false);
    }
  };

  if (editing) {
    return (
      <li className="rounded-card border border-border bg-bg-surface p-3">
        <VoiceThemeForm
          initial={{ name: editing.name, definition: editing.definition }}
          submitLabel={t('voicePage.themes.form.save')}
          onSubmit={async (values) => {
            await onEdit(editing, values);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      </li>
    );
  }

  const archived = theme.status === 'archived';
  return (
    <li className="flex flex-col gap-2 rounded-card border border-border bg-bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p
            className={
              archived
                ? 'text-sm font-medium text-fg-muted'
                : 'text-sm font-medium text-fg'
            }
          >
            {theme.name}
          </p>
          {theme.definition && (
            <p className="text-sm text-fg-muted">{theme.definition}</p>
          )}
        </div>
        <Badge
          size="sm"
          variant={
            theme.status === 'active'
              ? 'success'
              : theme.status === 'reserved'
                ? 'info'
                : 'default'
          }
        >
          {t(`voicePage.themes.status.${theme.status}`)}
        </Badge>
      </div>
      {canEdit && theme.status !== 'reserved' && (
        <div className="flex flex-wrap gap-2">
          {/* An archived theme keeps its name and definition (§4): restore
              it first, then edit. */}
          {!archived && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(theme)}
              disabled={pending}
            >
              {t('voicePage.themes.actions.edit')}
            </Button>
          )}
          {archived ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void toggleArchived(false)}
              disabled={pending || !canRestore}
              title={canRestore ? undefined : t('voicePage.themes.full')}
            >
              {t('voicePage.themes.actions.restore')}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void toggleArchived(true)}
              disabled={pending}
            >
              {t('voicePage.themes.actions.archive')}
            </Button>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </li>
  );
}
